import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Globe } from "lucide-react";
import { SEO_KEYWORDS, FEATURED_AREAS, ADDITIONAL_SERVICE_AREAS } from "../data/site";
import useContactInfo from "../lib/useContact";
import { useSocial, SocialIconRow } from "./SocialLinks";

export default function Footer() {
  const BUSINESS = useContactInfo();
  const social = useSocial();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#FBF8F3] text-[#111] border-t border-[#E7E2D9] pb-16 lg:pb-0"
    >
      <div className="px-6 md:px-12 lg:px-20 pt-20 pb-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <img
              src="/logo.png"
              alt="Zarco's Decks logo"
              data-testid="footer-logo"
              className="h-24 md:h-28 w-auto mb-8"
            />
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Family-Owned · Rome, Cartersville & Northwest Georgia
            </p>
            <p className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-5 text-[#111]">
              Custom decks. Built to last.
            </p>
            <p className="mt-6 text-[#4A4A4A] max-w-md leading-relaxed">
              Zarco's Decks designs and builds premium custom decks, TimberTech
              composite & PVC decking, and full outdoor living spaces across
              Metro Atlanta and North Georgia.
            </p>
            <Link
              to="/contact"
              data-testid="footer-free-estimate-btn"
              className="inline-block mt-8 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-8 py-4 text-sm font-semibold tracking-wide"
            >
              Get Your Free Estimate
            </Link>

            <div className="mt-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold mb-4">
                Follow Zarco's
              </p>
              <SocialIconRow social={social} testidPrefix="footer-social" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Contact
            </p>
            <ul className="mt-6 space-y-4 text-sm text-[#2A2A2A]">
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-1 text-[#99582A]" />
                <div>
                  <a href={BUSINESS.phoneMainHref} className="hover:text-[#99582A]" data-testid="footer-phone-main">
                    {BUSINESS.phoneMain}
                  </a>
                  <div className="text-[#888] text-xs">Call or text</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-1 text-[#99582A]" />
                <a href={BUSINESS.emailHref} className="hover:text-[#99582A] whitespace-nowrap" data-testid="footer-email">
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Globe size={16} className="mt-1 text-[#99582A]" />
                <span data-testid="footer-domain">{BUSINESS.website}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-1 text-[#99582A]" />
                <span>{BUSINESS.serviceArea}</span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Explore
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {["Home", "Services", "TimberTech", "Gallery", "About", "Contact"].map((l) => (
                <li key={l}>
                  <Link
                    to={l === "Home" ? "/" : `/${l.toLowerCase()}`}
                    className="text-[#2A2A2A] hover:text-[#99582A]"
                    data-testid={`footer-link-${l.toLowerCase()}`}
                  >
                    {l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Primary Areas
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#2A2A2A]" data-testid="footer-primary-areas">
              {FEATURED_AREAS.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold mt-8">
              Also Serving
            </p>
            <ul className="mt-4 space-y-2 text-sm text-[#4A4A4A]" data-testid="footer-secondary-areas">
              {ADDITIONAL_SERVICE_AREAS.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#E7E2D9]">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#888]">
            {SEO_KEYWORDS.join(" · ")}
          </p>
          <div className="mt-4 flex flex-col sm:flex-row justify-between gap-2 text-xs text-[#666]">
            <span>
              © {new Date().getFullYear()} {BUSINESS.name}. Family-Owned · Licensed & Insured.
            </span>
            <span className="flex flex-wrap gap-x-5 gap-y-1">
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#99582A]">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#99582A]">Website Terms</Link>
              <span>{BUSINESS.hours}</span>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
